import { useState } from 'react'
import useSWR from 'swr'
import { api, fetcher, auth } from '../api/client'

export default function ReportsPage() {
    const { data: prospects } = useSWR<any>('/api/prospects?pageSize=200', fetcher)
    const { data: reports, mutate: refreshReports } = useSWR<any[]>('/api/reports', fetcher)
    const [name, setName] = useState('')
    const [vertical, setVertical] = useState('')
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)

    function toggle(id: string) {
        const next = new Set(selected)
        next.has(id) ? next.delete(id) : next.add(id)
        setSelected(next)
    }

    async function generate() {
        if (!name || selected.size === 0) return
        setBusy(true); setMsg(null)
        try {
            const resp: any = await api('/api/reports/generate', {
                method: 'POST',
                body: JSON.stringify({ reportName: name, prospectIds: [...selected], vertical: vertical || null })
            })
            setMsg(resp.queued ? 'Report queued — refresh in a few seconds.' : 'Report generated.')
            setSelected(new Set())
            setName('')
            refreshReports()
        } catch (e: any) {
            setMsg(`Error: ${e.message}`)
        } finally {
            setBusy(false)
        }
    }

    function download(id: string, reportName: string) {
        /* needs the JWT — fetch+blob then trigger download */
        fetch(`/api/reports/${id}/pdf`, { headers: { Authorization: `Bearer ${auth.token()}` } })
            .then(r => r.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `${reportName}.pdf`
                a.click()
                URL.revokeObjectURL(url)
            })
    }

    return (
        <div>
            <h1>Executive Reports</h1>
            <p className="muted">Build a multi-page PDF with company overviews, decision makers, fit scores, and recommended outreach angles.</p>

            <div className="card">
                <h2>New Report</h2>
                <div className="split">
                    <div className="form-row">
                        <label>Report Name</label>
                        <input value={name} onChange={e => setName(e.target.value)} placeholder="Q1 Top Targets" data-testid="report-name" />
                    </div>
                    <div className="form-row">
                        <label>Vertical (optional)</label>
                        <input value={vertical} onChange={e => setVertical(e.target.value)} placeholder="SaaS, Fintech, etc." />
                    </div>
                </div>
                <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 4 }}>
                    <table>
                        <thead><tr><th></th><th>Name</th><th>Company</th><th>Score</th></tr></thead>
                        <tbody>
                            {prospects?.items?.map((p: any) => (
                                <tr key={p.id}>
                                    <td><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} /></td>
                                    <td>{p.name}</td>
                                    <td>{p.company}</td>
                                    <td>{p.leadScore ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="row" style={{ marginTop: 12 }}>
                    <button onClick={generate} disabled={busy || !name || selected.size === 0} data-testid="generate-report">
                        Generate ({selected.size} prospects)
                    </button>
                    {msg && <span className={msg.startsWith('Error') ? 'error' : 'success'} style={{ marginLeft: 12 }}>{msg}</span>}
                </div>
            </div>

            <div className="card">
                <h2>Past Reports</h2>
                {!reports ? <p className="muted">Loading...</p> :
                    reports.length === 0 ? <p className="muted">No reports yet.</p> :
                        <table>
                            <thead><tr><th>Name</th><th>Created</th><th>Prospects</th><th>Size</th><th></th></tr></thead>
                            <tbody>
                                {reports.map(r => (
                                    <tr key={r.id}>
                                        <td>{r.reportName}</td>
                                        <td className="muted">{new Date(r.createdAt).toLocaleString()}</td>
                                        <td>{r.prospectIds?.length ?? 0}</td>
                                        <td className="muted">{(r.pdfSizeBytes / 1024).toFixed(1)} KB</td>
                                        <td><button className="secondary" onClick={() => download(r.id, r.reportName)}>Download PDF</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                }
            </div>
        </div>
    )
}
